import { Box, Stack } from "@mui/material";
import { isEmpty } from "lodash-es";
import { useMemo, useRef, type FC } from "react";
import { uuidv4 } from "../../../../utils/uuid";
import { type ChatChannelName } from "../../../channels/types";
import { makeDefaultLexicalDesign } from "../../../lexical-chat-moment/make-default-lexical-design";
import type { ToolbarSettings } from "../../../lexical-email-moment/hooks/use-email-toolbar-settings";
import { useFilterTags } from "../../../merge-tag/components/use-filter-tags";
import type { Tags } from "../../../merge-tag/types";
import { DefaultBodyPlugins } from "../../../message/editor/default-body-plugins";
import { DefaultContentEditable } from "../../../message/editor/default-content-editable";
import { DefaultPlaceholder } from "../../../message/editor/default-placeholder";
import { useLexicalContext } from "../../../message/editor/lexical-context";
import { LexicalEditorWrapper } from "../../../message/editor/lexical-editor-wrapper";
import { GenericToolbar } from "../../../message/editor/plugins/generic-toolbar/generic-toolbar";
import { SurveyLinkToTargetSwitchPlugin } from "../../../message/editor/plugins/survey-link-to-target-switch-plugin";
import type { SerialisedLexicalEditorDesign } from "../../../message/editor/types/serialised-editor-design";
import { chatToolbarElements } from "../../../message/editor/utils/toolbar-elements/chat-toolbar-elements";
import { SlackAliasSelector } from "../../../slack/components/slack-alias-selector";
import type { ToTarget } from "../../../to/use-to-target";
import { useHandleFollowUpLexicalError } from "../../hooks/use-handle-follow-up-lexical-error";
import type { FollowUpChannelWithData } from "../../reducer/schema";

interface FollowUpMessageChatProps {
  channel: Extract<FollowUpChannelWithData, { channel: "slack" | "teams" }>;
  setChannelData: (data: FollowUpChannelWithData) => void;
  mergeTags: Tags;
  onChange: (val: SerialisedLexicalEditorDesign) => void;
  toTarget: ToTarget;
  design: SerialisedLexicalEditorDesign | null;
}

export const FollowUpMessageChat: FC<FollowUpMessageChatProps> = ({
  channel,
  setChannelData,
  mergeTags,
  onChange,
  toTarget,
  design,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // TODO: use actual id
  const onError = useHandleFollowUpLexicalError(uuidv4());

  const toolbarSettings = useMemo(() => {
    const elements = chatToolbarElements(channel.channel, toTarget.toTarget, {
      blacklistedElements: ["dynamicContent"],
    });

    return {
      channel: channel.channel,
      topLevelElements: elements.topLevel,
      floatingElements: elements.floating,
    };
  }, [channel.channel, toTarget]);

  const filterTags = useFilterTags(
    mergeTags.filter((x) => x.version === 2),
    toTarget,
  );

  return (
    <Stack direction="column" spacing={2}>
      {channel.channel === "slack" && (
        <SlackAliasSelector
          value={channel.channelData.slack_alias_id ?? null}
          onChange={(slack_alias_id) =>
            setChannelData({
              channel: "slack",
              channelData: { slack_alias_id },
            })
          }
          toTarget={toTarget.toTarget}
        />
      )}
      <LexicalEditorWrapper
        channelName={channel.channel}
        toChannel={toTarget.toTarget === "channel"}
        disabled={false}
        onError={(...args) => void onError(...args).catch(console.error)}
        error={undefined}
        filterTags={filterTags}
        design={isEmpty(design) ? makeDefaultLexicalDesign("chat") : design}
        format="chat"
        renderLayout={(editor) => <Box ref={ref}>{editor}</Box>}
        slots={{
          contentEditable: ({ focusEditor, onRef }) => (
            <DefaultContentEditable
              focusEditor={focusEditor}
              onRef={onRef}
              channel={channel.channel}
              disabled={false}
            />
          ),
          placeholder: <DefaultPlaceholder />,
          plugins: {
            head: () => (
              <ChatHeadPlugins
                channel={channel.channel}
                toolbarSettings={toolbarSettings}
                disabled={false}
              />
            ),
            body: (historyState) => (
              <>
                <DefaultBodyPlugins
                  auto_focus={false}
                  channel={channel.channel}
                  format="chat"
                  disabled={false}
                  onChange={(val) => onChange(val.toJSON())}
                  editorScrollRef={ref}
                  toolbarSettings={toolbarSettings}
                  historyState={historyState}
                  v1Tags={mergeTags.filter((x) => x.version === 1)}
                  forceSimple
                />
                <SurveyLinkToTargetSwitchPlugin toTarget={toTarget.toTarget} />
              </>
            ),
          },
        }}
      />
    </Stack>
  );
};

const ChatHeadPlugins: FC<{
  channel: ChatChannelName;
  toolbarSettings: ToolbarSettings;
  disabled?: boolean;
}> = ({ toolbarSettings, disabled }) => {
  // This will eventually be rendered by the LexicalEditor calling the slot, so we can
  // use the context here
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  return (
    <>
      <GenericToolbar
        channel={toolbarSettings.channel}
        topLevelElements={toolbarSettings.topLevelElements}
        disabled={!!disabled}
        openModal={handleOpen}
      />
    </>
  );
};
